import * as Icons from "heroicons-react";
import { signInUser } from "../../Utility/Auth";
import { useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useInvoiceWiseProductQuery } from "../../../services/reportsApi";
import { useWarehouseQuery } from "../../../services/warehouseApi";
import WareHouseDW from "../../Common/CustomSelect/WareHouseDW";

const InvoiceWiseProductReport = () => {
  const auth = signInUser();

  const [warehouse, setWarehouse] = useState("allWh");
  const [invoiceId, setInvoiceId] = useState("");

  const [startDate, setStartDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [endDate, setEndDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const { aamarId } = auth;
  const lang = useSelector((state) => state.languageReducer);
  const { data: salesData } = useInvoiceWiseProductQuery({
    startDate,
    endDate,
    warehouse,
    aamarId,
    invoiceId,
  });

  // console.log("User Wise Sales Report Data:", salesData);

  // fine warehouse Name
  const [whName, setWhName] = useState("All Warehouses");

  const { data: wh, refetch } = useWarehouseQuery(warehouse);

  // Call refetch only if user?.warehouse changes
  useEffect(() => {
    if (warehouse) {
      refetch();
    }
  }, [warehouse, refetch]);

  // Update warehouse name when data changes
  useEffect(() => {
    if (wh?.name) {
      setWhName(wh.name); // Directly set the name, no need for optional chaining here
    }
  }, [wh]);
  // console.log("Customer Wise Sales Report Data:", salesData);

  // Export CSV
  const handleExportCSV = () => {
    if (!salesData || salesData.length === 0) {
      alert("No data available for export.");
      return;
    }

    const start = new Date(startDate).toLocaleString();
    const end = new Date(endDate).toLocaleString();
    // const end = salesData[salesData.length - 1]?.createdAt;
    const generatedOn = new Date().toLocaleString();
    const generatedBy = auth?.name || "Aamar Dokan";

    const storeName =
      auth?.storeSettings?.storeName?.toUpperCase() || "Aamar Dokan";
    const warehouseName = whName;
    const reportTitle = "Invoice Wise Product Report";

    const headerInfo = [
      `,Store: ${storeName},,,,,Warehouse: ${warehouseName}`,
      `,Report: ${reportTitle},,,,,Date Range: ${start} to ${end}`,
      `,Generated On: ${generatedOn},,,,Generated By: ${generatedBy}`,
      "",
      "",
    ];

    const csvTableHeader = [
      "S/N",
      "Invoice No",
      "Date",
      "Customer",
      "Product",
      "Article Code",
      "EAN",
      "Qty",
      "Unit",
      "MRP",
      "TP",
      "VAT",
      "Total",
      "Warehouse",
    ].join(",");

    let totalQty = 0;
    let totalMRP = 0;
    let totalTP = 0;
    let totalVAT = 0;
    let totalTotal = 0;
    // let totalGrossProfit = 0;

    const csvRows = salesData.map((row, index) => {
      const qty = Number(row?.qty || 0);
      const mrp = Number(row?.mrp || 0);
      const tp = Number(row?.tp || 0);
      const vat = Number(row?.vat || 0);
      const total = Number(row?.total || 0);

      // Assuming Gross Profit = Total - (MRP * Qty)
      // const grossProfit = Number(row?.total || 0) -  Number(row?.mrp || 0) * Number(row?.qty || 0);

      totalQty += qty;
      totalMRP += mrp;
      totalTP += tp;
      totalVAT += vat;
      totalTotal += total;
      // totalGrossProfit += grossProfit;

      return [
        index + 1,
        row?.invoiceId || "",
        row?.date || "",
        row?.customer || "",
        row?.name || "",
        row?.article_code || "",
        row?.ean || "",
        qty,
        row?.unit || "",
        mrp,
        tp,
        vat,
        total,
        // grossProfit.toFixed(2),
        row?.warehouse || "",
      ].join(",");
    });

    const summaryLabelRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      "** TOTAL SUMMARY **",
    ].join(",");

    const summaryHeaderRow = [
      "",
      "",
      "",
      "",
      "",
      "Total Qty",
      "Total MRP",
      "Total TP",
      "Total VAT",
      "Total Sales",
      "Total Gross Profit",
    ].join(",");

    const summaryDataRow = [
      "",
      "",
      "",
      "",
      "",
      totalQty,
      totalMRP,
      totalTP,
      totalVAT,
      totalTotal,
      // totalGrossProfit.toFixed(2),
    ].join(",");

    const csvContent = [
      ...headerInfo,
      csvTableHeader,
      ...csvRows,
      "",
      "",
      summaryLabelRow,
      summaryHeaderRow,
      summaryDataRow,
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "InvoiceWiseProductReport.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // export PDF
  const handleExportPDF = () => {
    const doc = new jsPDF("landscape");

    if (salesData?.length === 0) {
      alert("No sales data found.");
      return;
    }

    const options = { month: "numeric", day: "numeric", year: "numeric" };
    const formattedStart = new Date(startDate).toLocaleDateString(
      "en-US",
      options
    );
    const formattedEnd = new Date(endDate).toLocaleDateString("en-US", options);
    const reportGeneratedAt = new Date().toLocaleString();

    const storeName =
      auth?.storeSettings?.storeName?.toUpperCase() || "Aamar Dokan";
    const warehouseName = whName;
    const reportName = "Invoice Wise Product Report";

    // Title
    doc.setFontSize(16);
    doc.text(storeName, 140, 15, { align: "center" });

    doc.setFontSize(12);
    doc.text(reportName, 140, 22, { align: "center" });

    // Header info - Justify between
    doc.setFontSize(10);
    doc.text(`Store: ${storeName}`, 14, 28);
    doc.text(`Warehouse: ${warehouseName}`, 14, 33);
    doc.text(`Date: ${formattedStart} - ${formattedEnd}`, 14, 39);
    // doc.text(`Report: ${reportName}`, 140, 28, { align: "center" });
    doc.text(`Generated At: ${reportGeneratedAt}`, 280, 28, { align: "right" });
    doc.text(`Generated By: ${auth?.name}`, 280, 33, { align: "right" });

    // Table header
    const headers = [
      [
        "S/N",
        "Invoice No",
        "Date",
        "Customer",
        "Product",
        "Article Code",
        "EAN",
        "Qty",
        "Unit",
        "MRP",
        "TP",
        "VAT",
        "Total",
        "Warehouse",
      ],
    ];
    const data = salesData?.map((item, index) => [
      index + 1,
      item?.invoiceId || "",
      item?.date || "",
      item?.customer || "",
      item?.name || "",
      item?.article_code || "",
      item?.ean || "",
      item?.qty || 0,
      item?.unit || "-",
      item?.mrp || 0,
      item?.tp || 0,
      item?.vat || 0,
      item?.total || 0,
      item?.warehouse || "",
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 45,
      theme: "grid",
      styles: {
        fontSize: 9,
        halign: "center",
      },
      headStyles: {
        fillColor: [242, 101, 45], // tomato background
        textColor: [255, 255, 255], // white text
        fontStyle: "bold",
      },
    });

    // === Summary Box ===
    const finalY = doc.lastAutoTable.finalY + 10;
    const lineHeight = 7;
    const boxHeight = 6 * lineHeight + 10;
    const boxWidth = 100;

    const pageWidth = doc.internal.pageSize.getWidth();
    const boxX = pageWidth - boxWidth - 15;
    const boxY = finalY;

    // Draw dotted border
    doc.setLineDashPattern([1, 1], 0);
    doc.rect(boxX, boxY, boxWidth, boxHeight);
    doc.setLineDashPattern([], 0); // reset to solid

    let y = boxY + 10;
    const labelX = boxX + 5;
    const valueX = boxX + boxWidth - 5;
    doc.setFontSize(9);

    const sumField = (arr, field) =>
      arr.reduce((sum, item) => sum + (parseFloat(item[field]) || 0), 0);

    const totalQty = sumField(salesData, "qty");
    const totalMRP = sumField(salesData, "mrp");
    const totalTP = sumField(salesData, "tp");
    const totalVAT = sumField(salesData, "vat");
    const totalSales = sumField(salesData, "total");
    const totalGP = totalSales - totalTP;

    const gpPercent =
      totalSales > 0 ? ((totalGP / totalSales) * 100).toFixed(2) : "0.00";

    doc.text("Total Qty:", labelX, y);
    doc.text(`${totalQty}`, valueX, y, { align: "right" });

    y += lineHeight;
    doc.text("Total MRP:", labelX, y);
    doc.text(`${totalMRP.toFixed(2)}`, valueX, y, { align: "right" });

    y += lineHeight;
    doc.text("Total TP:", labelX, y);
    doc.text(`${totalTP.toFixed(2)}`, valueX, y, { align: "right" });

    y += lineHeight;
    doc.text("Total VAT:", labelX, y);
    doc.text(`${totalVAT.toFixed(2)}`, valueX, y, { align: "right" });

    y += lineHeight;
    doc.text("Total Sales:", labelX, y);
    doc.text(`${totalSales.toFixed(2)}`, valueX, y, { align: "right" });

    y += lineHeight;
    doc.text("Gross Profit %:", labelX, y);
    doc.text(`${gpPercent}%`, valueX, y, { align: "right" });

    // Save the PDF
    doc.save("InvoiceWiseProductReport.pdf");
  };

  //warehouse
  const handleOnchangeWareHouseFrom = (e) => {
    if (e.option !== "no-warehouse") {
      setWarehouse(e.option);
    } else {
      setWarehouse("allWh");
    }
  };

  return (
    <div>
      <div>
        <h2 className="mb-3">
          <Icons.ShoppingCartOutline size={35} /> Invoice Wise Product Report
        </h2>
      </div>

      <div
        className="mt-3 w-40 p-3"
        style={{ backgroundColor: "#f1f3f5", borderRadius: "8px" }}
      >
        <div className="d-flex mt-2 mb-2 align-items-center justify-content-center gap-2">
          <DatePicker
            selected={new Date(startDate)}
            className="form-control w-100"
            onChange={(date) =>
              setStartDate(format(new Date(date), "MM-dd-yyyy"))
            }
          />
          <DatePicker
            selected={new Date(endDate)}
            className="form-control me-4"
            onChange={(date) =>
              setEndDate(format(new Date(date), "MM-dd-yyyy"))
            }
          />
        </div>

        {/* Warehouse Dropdown */}
        {auth?.type === "admin" && (
          <div className="my-3">
            <WareHouseDW
              id="warehouse"
              name="warehouse"
              handleOnChange={handleOnchangeWareHouseFrom}
              className="form-control"
            />
          </div>
        )}

        {/* 🔍 invoice Field */}
        <div className="mb-3">
          <input
            type="text"
            className="form-control"
            placeholder="Invoice ID ..."
            value={invoiceId}
            onChange={(e) => setInvoiceId(e.target.value)}
          />
        </div>

        {/* Buttons */}
        <div className="d-flex gap-3 justify-content-center align-items-center text-center">
          <button
            className="btn btn-dark w-100 mt-3"
            style={{ borderRadius: "5px" }}
            onClick={handleExportCSV}
          >
            <Icons.DownloadOutline size={22} /> Download CSV
          </button>

          <button
            className="btn btn-dark w-100 mt-3"
            style={{ borderRadius: "5px" }}
            onClick={handleExportPDF}
          >
            <Icons.DocumentOutline size={22} /> Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceWiseProductReport;
